import FbButton from 'components/FbUI/FbButton'
import { filterTriggerClasses } from 'components/FbUI/filterTriggerClasses'
import { cn } from 'components/UI/cn'
import React from 'react'
import { PiCaretDown, PiCaretUp } from 'react-icons/pi'

export interface DataTableFilterButtonsProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'title' | 'type'
> {
  isActive: boolean
  open: boolean
  title: string
  /** Optional count for the green selection bubble (multi-select only). For
   *  range / date / zip / single-select, leave this undefined — the selected
   *  pill color is the only signal needed, and stuffing a value like
   *  "$5,000 – $9,999" into a bubble would overflow the pill. */
  count?: number
  twoLineLayout?: boolean
}

const FilterButtonComponent = React.forwardRef<
  HTMLButtonElement,
  DataTableFilterButtonsProps
>((props, ref) => {
  const {
    isActive,
    open,
    title,
    count,
    twoLineLayout: _twoLineLayout,
    className,
    ...rest
  } = props
  const showCount = isActive && count != null && count > 0

  return (
    <FbButton
      ref={ref}
      variant={'secondary'}
      type="button"
      className={cn(filterTriggerClasses({ open, isActive }), className)}
      // Spread the rest — Radix's `DropdownMenuTrigger asChild` merges its
      // own onClick / onPointerDown / aria-expanded / aria-controls /
      // data-state onto this child; without forwarding them the trigger
      // never opens.
      {...rest}
    >
      <div
        className={cn(
          'w-full flex items-center gap-2',
          // Reserve room on the right for the FilterCloseButton X when active.
          isActive && !open && 'pr-6'
        )}
      >
        <span className="text-sm font-medium truncate flex-1 text-left">
          {title}
        </span>
        {showCount && (
          <span
            className={cn(
              'inline-flex items-center justify-center rounded-full',
              'h-5 min-w-[20px] px-1.5 text-xs font-semibold tabular-nums',
              'bg-brand-bright text-white'
            )}
          >
            {count}
          </span>
        )}
        {/* Open caret stays visible while the popover is open. When the
            filter is active+closed, the FilterCloseButton X overlays this
            slot (controlled by parent), so we hide the caret to avoid
            double-icons. */}
        {open ? (
          <PiCaretUp size={14} className="flex-shrink-0" />
        ) : !isActive ? (
          <PiCaretDown size={14} className="flex-shrink-0 text-neutral-500" />
        ) : null}
      </div>
    </FbButton>
  )
})
FilterButtonComponent.displayName = 'FilterButton'

export const FilterButton = FilterButtonComponent
